/**
 * Sheet laterale per il drill-down dashboard (F16.1).
 *
 * Riceve un set di preventivi gia` filtrati + titolo/descrizione e li
 * elenca in modo compatto. Ogni riga e` cliccabile e naviga al detail
 * del preventivo. Cosi` da qualunque KPI/grafico l'utente puo` vedere
 * "quali preventivi compongono questo numero" senza lasciare la
 * dashboard.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, Trophy, XCircle, Send, FileEdit } from 'lucide-react';
import EstimateStatusBadge from '@/components/estimates/EstimateStatusBadge';

export interface DrillDownEstimate {
  id: string;
  name: string;
  status: string;
  total_amount: number | string | null;
  created_at: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projects?: { name?: string | null } | null;
}

interface DashboardDrillDownSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Preventivi gia` filtrati per la metrica. */
  estimates: DrillDownEstimate[];
  /** Icona accanto al titolo del sheet (opzionale). */
  icon?: React.ReactNode;
}

function euro(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  return `€ ${Math.round(Number.isFinite(n) ? n : 0).toLocaleString('it-IT')}`;
}

function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  draft: <FileEdit className="h-3.5 w-3.5 text-slate-500" />,
  sent: <Send className="h-3.5 w-3.5 text-amber-600" />,
  pending: <Send className="h-3.5 w-3.5 text-amber-600" />,
  won: <Trophy className="h-3.5 w-3.5 text-emerald-600" />,
  contracted: <Trophy className="h-3.5 w-3.5 text-emerald-600" />,
  approved: <Trophy className="h-3.5 w-3.5 text-emerald-600" />,
  lost: <XCircle className="h-3.5 w-3.5 text-red-600" />,
};

const DashboardDrillDownSheet: React.FC<DashboardDrillDownSheetProps> = ({
  open, onOpenChange, title, description, estimates, icon,
}) => {
  const navigate = useNavigate();
  const total = estimates.reduce((s, e) => s + Number(e.total_amount ?? 0), 0);

  const goTo = (estimateId: string) => {
    onOpenChange(false);
    // Route canonica del detail preventivo (vedi App.tsx).
    navigate(`/estimates/${estimateId}/manage`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {icon}
            {title}
          </SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {estimates.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
              Nessun preventivo in questa metrica nel periodo selezionato.
            </div>
          ) : (
            <>
              {/* Riepilogo */}
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded border mb-3">
                <span className="text-sm font-medium">
                  {estimates.length} {estimates.length === 1 ? 'preventivo' : 'preventivi'}
                </span>
                <span className="text-sm font-bold tabular-nums">{euro(total)}</span>
              </div>

              {/* Lista preventivi */}
              {estimates
                .slice()
                .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0))
                .map(e => (
                  <button
                    key={e.id}
                    onClick={() => goTo(e.id)}
                    className="w-full text-left flex items-start gap-3 p-3 rounded border bg-card hover:bg-muted/30 transition-colors group"
                  >
                    <div className="mt-0.5">{STATUS_ICONS[e.status] ?? <FileText className="h-3.5 w-3.5" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-medium text-sm truncate">{e.name}</span>
                        <span className="font-bold text-sm tabular-nums shrink-0">{euro(e.total_amount)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <EstimateStatusBadge status={e.status} size="sm" />
                        {e.projects?.name && (
                          <>
                            <span>·</span>
                            <span className="truncate">{e.projects.name}</span>
                          </>
                        )}
                        <span>·</span>
                        <span>{formatDate(e.created_at)}</span>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
                  </button>
                ))}
            </>
          )}
        </div>

        {estimates.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => onOpenChange(false)}
            >
              Chiudi
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default DashboardDrillDownSheet;
